/**
 * Student leaks page (/portal/leaks).
 * Current disputes (received + in_progress) up top, archive (success + failure)
 * below. Top-right "Report a leak" button opens a modal with ReportLeakForm.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, ArrowRight, WarningCircle } from "@phosphor-icons/react";
import { leaksApi, isClosed } from "../../api/leaks";
import PortalPageHeader from "../../components/portal/PortalPageHeader";
import PortalEmptyState from "../../components/portal/PortalEmptyState";
import LeakStatusBadge from "../../components/leaks/LeakStatusBadge";
import ReportLeakForm from "../../components/leaks/ReportLeakForm";
import Modal from "../../components/ui-shared/Modal";

function LeakRow({ leak, basePath }) {
  return (
    <Link
      to={`${basePath}/${leak.id}`}
      data-testid={`leak-row-${leak.id}`}
      className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-slate-900 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">
          {leak.title}
        </p>
        <p className="text-xs text-slate-500 truncate">{leak.url}</p>
      </div>
      <LeakStatusBadge status={leak.status} />
      <ArrowRight
        size={16}
        weight="bold"
        className="text-slate-400 group-hover:text-slate-900 group-hover:translate-x-1 transition-all"
      />
    </Link>
  );
}

export default function PortalLeaks() {
  const { t } = useTranslation();
  const [leaks, setLeaks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await leaksApi.listOwn();
      setLeaks(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const current = leaks.filter((l) => !isClosed(l.status));
  const archive = leaks.filter((l) => isClosed(l.status));

  return (
    <div data-testid="portal-leaks-page" className="space-y-10">
      <PortalPageHeader
        kickerKey="portal.placeholder.kicker"
        titleKey="portal.nav.leaks"
        subtitleKey="portal.pages.leaks_desc"
        right={
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="portal-leaks-report-button"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <Plus size={14} weight="bold" />
            {t("leaks.report_button")}
          </button>
        }
      />

      {loading ? (
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      ) : leaks.length === 0 ? (
        <PortalEmptyState
          variant="dashed"
          icon={WarningCircle}
          titleKey="leaks.empty_title"
          descKey="leaks.empty_desc"
        />
      ) : (
        <>
          <section data-testid="portal-leaks-current">
            <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900 mb-4">
              {t("leaks.current")}{" "}
              <span className="text-slate-400 font-normal text-sm">
                ({current.length})
              </span>
            </h2>
            {current.length === 0 ? (
              <p className="text-sm text-slate-500">{t("leaks.no_current")}</p>
            ) : (
              <div className="space-y-2">
                {current.map((l) => (
                  <LeakRow key={l.id} leak={l} basePath="/portal/leaks" />
                ))}
              </div>
            )}
          </section>

          <section data-testid="portal-leaks-archive">
            <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900 mb-4">
              {t("leaks.archive")}{" "}
              <span className="text-slate-400 font-normal text-sm">
                ({archive.length})
              </span>
            </h2>
            {archive.length === 0 ? (
              <p className="text-sm text-slate-500">{t("leaks.no_archive")}</p>
            ) : (
              <div className="space-y-2">
                {archive.map((l) => (
                  <LeakRow key={l.id} leak={l} basePath="/portal/leaks" />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t("leaks.report_modal_title")}
        testId="portal-report-leak-modal"
      >
        <ReportLeakForm
          mode="student"
          onCancel={() => setModalOpen(false)}
          onCreated={() => {
            setModalOpen(false);
            load();
          }}
        />
      </Modal>
    </div>
  );
}
